'use client';

import React, { useState, useEffect, FormEvent, useRef, useMemo } from 'react';
import { Button, Form, Alert, ButtonGroup, Table } from 'react-bootstrap';
import CtaButton from '@/componentv2/button/button';
import { useRouter } from 'next/navigation';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FaThLarge,
  FaList,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import './SearchResultClient.scss';
import Link from 'next/link';
import ResultImage from './ResultImage';
import Image from 'next/image';

interface SearchResultClientProps {
  formValue: string;
  endpoint: string;
}

interface SearchResult {
  id: string;
  title: string;
  group: string;
  earliestDate: string;
  latestDate: string;
  link: string;
  thumbnail?: string; // Optional thumbnail URL
}

type SortKey = 'title' | 'group' | 'earliestDate' | 'latestDate';

interface SortConfig {
  key: SortKey;
  direction: 'asc' | 'desc';
}

type ViewMode = 'grid' | 'list';

const SearchResultClient: React.FC<SearchResultClientProps> = ({
  formValue,
  endpoint,
}) => {
  const [searchValue, setSearchValue] = useState(formValue);
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const router = useRouter();
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) {
        return { key, direction: 'asc' };
      }
      // toggle direction
      const nextDirection = prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction: nextDirection };
    });
  };

  const sortedResults = useMemo(() => {
    if (!results) return results;
    if (!sortConfig) return results;

    const { key, direction } = sortConfig;

    const parseMaybeDate = (val: string) => {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d.getTime();
    };

    const collator = new Intl.Collator(undefined, {
      sensitivity: 'base',
      numeric: true,
    });

    return [...results].sort((a, b) => {
      // Use indexed access with type safety
      const aRecord: Record<SortKey, string> = {
        title: a.title,
        group: a.group,
        earliestDate: a.earliestDate,
        latestDate: a.latestDate,
      };
      const bRecord: Record<SortKey, string> = {
        title: b.title,
        group: b.group,
        earliestDate: b.earliestDate,
        latestDate: b.latestDate,
      };

      let aVal: string | number | null = aRecord[key];
      let bVal: string | number | null = bRecord[key];

      // For date keys, try to parse
      if (key === 'earliestDate' || key === 'latestDate') {
        aVal = typeof aVal === 'string' ? parseMaybeDate(aVal) : aVal;
        bVal = typeof bVal === 'string' ? parseMaybeDate(bVal) : bVal;
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1; // push nulls to bottom
      if (bVal == null) return -1;

      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = collator.compare(String(aVal), String(bVal));
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [results, sortConfig]);

  const renderSortIcon = (key: SortKey) => {
    if (!sortConfig || sortConfig.key !== key)
      return <FaSort aria-hidden="true" className="sort-icon" />;
    if (sortConfig.direction === 'asc')
      return <FaSortUp aria-hidden="true" className="sort-icon" />;
    return <FaSortDown aria-hidden="true" className="sort-icon" />;
  };

  // Function to call the API and get results
  const fetchSearchResults = async (value: string) => {
    const normalizedValue = value.trim().toLocaleLowerCase();

    if (!normalizedValue || !endpoint) return;

    setLoading(true);
    setError(null);

    try {
      // Call the actual API endpoint
      const apiUrl = `${endpoint.replace(/\/$/, '')}/${encodeURIComponent(normalizedValue)}`;
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error('API error');
      const json = await response.json();
      const apiResults = json?.returnData?.data?.results || [];
      setResults(
        apiResults.map((item: Record<string, unknown>, idx: number) => ({
          id: item.ID?.toString() || idx.toString(),
          title: item.TEMPTITLE || item.TITLE || '',
          group: item.COMMUNITY || '',
          earliestDate: item.STARTDATE || '',
          latestDate: item.ENDDATE || '',
          link: item.URLID ? `${window.location.origin}/go/${item.URLID}` : '#',
          thumbnail: item.thumbnail,
        }))
      );
    } catch {
      setError('An error occurred while searching. Please try again.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  // Call the API on initial render if formValue exists
  useEffect(() => {
    if (!formValue) return;

    fetchSearchResults(formValue);

    // fetchSearchResults is stable and does not change
  }, [formValue]);

  // Handle form submission for new searches
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // Validate captcha
    if (!captchaValue) {
      setShowCaptchaError(true);
      return;
    }

    // Update the URL with the new search value
    const normalizedSearchValue = searchValue.trim().toLocaleLowerCase();
    const params = new URLSearchParams();
    params.append('formValue', normalizedSearchValue);
    params.append('endpoint', endpoint);

    // Update the URL without full page reload
    router.push(`/findasignupresult?${params.toString()}`, { scroll: false });

    // Fetch new results
    fetchSearchResults(normalizedSearchValue);

    // Reset captcha for next search
    setCaptchaValue(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
    setShowCaptchaError(false);
  };

  const toggleViewMode = (mode: ViewMode) => {
    setViewMode(mode);
  };

  return (
    <div className="search-results">
      {loading && (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && results === null && (
        <div className="no-results">
          <p>Enter an email address to search for sign ups.</p>
        </div>
      )}

      {!loading && results !== null && results.length === 0 && (
        <div className="no-results">
          <h3>No sign ups found</h3>
          <p>
            We couldn&apos;t find any sign ups for {formValue}. Try searching
            again with a different email address.
          </p>

          <div className="signup-card mb-4">
            <Form onSubmit={handleSubmit} className="signup-search-form">
              {/* Email Input */}
              <Form.Group className="mb-3">
                {/* <Form.Label>Search by sign up creator&apos;s email</Form.Label> */}

                <div className="input-wrapper">
                  <Form.Control
                    ref={emailInputRef}
                    type="email"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Enter email address"
                    required
                    autoComplete="off"
                  />

                  {/* Clear button */}
                  {searchValue && (
                    <span
                      className="clear-btn"
                      role="button"
                      aria-label="Clear email"
                      onClick={() => {
                        setSearchValue('');
                        emailInputRef.current?.focus();
                      }}
                    >
                      <Image
                        src="/n_images/closebtn.svg"
                        alt="Clear"
                        width={16}
                        height={16}
                        priority={true}
                      />
                    </span>
                  )}
                </div>
              </Form.Group>

              {/* reCAPTCHA */}
              <div className="recaptcha-container mb-3 text-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LdBcCEUAAAAABcH2WFGbc2ZfOyt60UapRX5EXOM"
                  onChange={handleCaptchaChange}
                  aria-required="true"
                />
                {showCaptchaError && (
                  <Alert variant="danger" className="mt-2 py-2 small">
                    Please complete the CAPTCHA verification
                  </Alert>
                )}
              </div>

              {/* Submit */}
              <CtaButton
                variant="primary"
                type="submit"
                className="w-100 btn-secondary"
              >
                Search Again
              </CtaButton>
            </Form>
          </div>
        </div>
      )}

      {!loading && results && results.length > 0 && (
        <div>
          <div className="signup-card mb-4">
            <Form onSubmit={handleSubmit} className="signup-search-form">
              {/* Email Input */}
              <Form.Group className="mb-3">
                {/* <Form.Label>Search by sign up creator&apos;s email</Form.Label> */}

                <div className="input-wrapper">
                  <Form.Control
                    ref={emailInputRef}
                    type="email"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Enter email address"
                    required
                    autoComplete="off"
                  />

                  {/* Clear button */}
                  {searchValue && (
                    <span
                      className="clear-btn"
                      role="button"
                      aria-label="Clear email"
                      onClick={() => {
                        setSearchValue('');
                        emailInputRef.current?.focus();
                      }}
                    >
                      <Image
                        src="/n_images/closebtn.svg"
                        alt="Clear"
                        width={16}
                        height={16}
                        priority={true}
                      />
                    </span>
                  )}
                </div>
              </Form.Group>

              {/* reCAPTCHA */}
              <div className="recaptcha-container mb-3 text-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LdBcCEUAAAAABcH2WFGbc2ZfOyt60UapRX5EXOM"
                  onChange={handleCaptchaChange}
                  aria-required="true"
                />
                {showCaptchaError && (
                  <Alert variant="danger" className="mt-2 py-2 small">
                    Please complete the CAPTCHA verification
                  </Alert>
                )}
              </div>

              {/* Submit */}
              <CtaButton
                variant="primary"
                type="submit"
                className="w-100 btn-secondary"
              >
                Search
              </CtaButton>
            </Form>
          </div>

          <div className="results-header">
            <div className="results-info">
              <h3 className="results-heading">
                {results ? results.length : ''} Sign Ups Available
              </h3>
            </div>
            <div className="view-toggle">
              <ButtonGroup aria-label="View mode toggle">
                {viewMode === 'list' ? (
                  <Button
                    variant={'outline'}
                    onClick={() => toggleViewMode('grid')}
                    aria-label="Grid view"
                  >
                    <FaThLarge aria-hidden="true" />
                  </Button>
                ) : (
                  <Button
                    variant={'outline'}
                    onClick={() => toggleViewMode('list')}
                    aria-label="List view"
                  >
                    <FaList aria-hidden="true" />
                  </Button>
                )}
              </ButtonGroup>
            </div>
          </div>
          {viewMode === 'grid' ? (
            <div className={`results-container ${viewMode}`}>
              {results.map((result) => (
                <div key={result.id}>
                  <Link href={result.link} target="_blank">
                    <div className="result-item">
                      <div className="image-wrapper">
                        <ResultImage
                          thumbnail={result.thumbnail}
                          title={result.title}
                        />
                      </div>
                      <div className="card-text-inline">
                        <span className="group-name">{result.group}</span>
                        <p className="signup-title">{result.title}</p>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="row">
              <div className={`results-table ${viewMode}`}>
                <Table>
                  <thead>
                    <tr>
                      <th
                        aria-sort={
                          sortConfig?.key === 'title'
                            ? sortConfig.direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          className="sortable-header-btn"
                          onClick={() => handleSort('title')}
                        >
                          <span>Title {renderSortIcon('title')}</span>
                        </button>
                      </th>
                      <th
                        aria-sort={
                          sortConfig?.key === 'group'
                            ? sortConfig.direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          className="sortable-header-btn"
                          onClick={() => handleSort('group')}
                        >
                          <span>Group {renderSortIcon('group')}</span>
                        </button>
                      </th>
                      <th
                        aria-sort={
                          sortConfig?.key === 'earliestDate'
                            ? sortConfig.direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          className="sortable-header-btn"
                          onClick={() => handleSort('earliestDate')}
                        >
                          <span>
                            Earliest Date {renderSortIcon('earliestDate')}
                          </span>
                        </button>
                      </th>
                      <th
                        aria-sort={
                          sortConfig?.key === 'latestDate'
                            ? sortConfig.direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          className="sortable-header-btn"
                          onClick={() => handleSort('latestDate')}
                        >
                          <span>
                            Latest Date {renderSortIcon('latestDate')}
                          </span>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sortedResults || []).map((result) => (
                      <tr key={result.id}>
                        <td>
                          <Link href={result.link} target="_blank">
                            {result.title}
                          </Link>
                        </td>
                        <td>{result.group}</td>
                        <td>{result.earliestDate}</td>
                        <td>{result.latestDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchResultClient;
